import { useState, useEffect } from 'react'
import { supabase } from '../supabase'
import AdminNav from '../components/AdminNav'

const BLUE   = '#1877F2'
const TEXT   = '#050505'
const MUTED  = '#65676B'
const BORDER = '#E4E6EB'
const BG     = '#F0F2F5'
const WHITE  = '#FFFFFF'
const GREEN  = '#16A34A'
const RED    = '#DC2626'

const VERIFY_STYLE = {
  verified:   { bg: '#F0FDF4', color: GREEN, label: 'Verified'   },
  pending:    { bg: '#EFF6FF', color: BLUE,  label: 'Pending'    },
  unverified: { bg: '#FEF2F2', color: RED,   label: 'Rejected'   },
}

function Icon({ path, size = 15, color = MUTED, sw = 1.8 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
      stroke={color} strokeWidth={sw} strokeLinecap="round" strokeLinejoin="round">
      <path d={path} />
    </svg>
  )
}

const IC = {
  search: 'M21 21l-4.35-4.35M11 19a8 8 0 100-16 8 8 0 000 16z',
  fb:     'M18 2h-3a5 5 0 00-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 011-1h3z',
  ext:    'M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14',
  check:  'M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z',
  x:      'M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z',
}

function DocRow({ label, value, mono }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', padding: '9px 12px', background: value ? '#F0FDF4' : BG, borderRadius: '8px', marginBottom: '6px', gap: '12px' }}>
      <span style={{ fontSize: '12px', color: MUTED, flexShrink: 0 }}>{label}</span>
      <span style={{ fontSize: '12px', fontWeight: 600, color: value ? GREEN : MUTED, fontFamily: mono ? 'JetBrains Mono, monospace' : 'inherit', textAlign: 'right', wordBreak: 'break-word' }}>
        {value || 'Not provided'}
      </span>
    </div>
  )
}

function AdminVerifications() {
  const [sellers, setSellers]   = useState([])
  const [loading, setLoading]   = useState(true)
  const [selected, setSelected] = useState(null)
  const [updating, setUpdating] = useState(false)
  const [filter, setFilter]     = useState('pending')
  const [search, setSearch]     = useState('')
  const [saved, setSaved]       = useState('')

  useEffect(() => { fetchAll() }, [])

  const fetchAll = async () => {
    setLoading(true)
    const { data } = await supabase
      .from('sellers').select('*')
      .not('verification_requested_at', 'is', null)
      .order('verification_requested_at', { ascending: false })
    setSellers(data || [])
    setLoading(false)
  }

  const updateVerification = async (id, status) => {
    setUpdating(true)
    await supabase.from('sellers').update({ verification_status: status }).eq('id', id)
    await fetchAll()
    setSelected(prev => prev?.id === id ? { ...prev, verification_status: status } : prev)
    setUpdating(false)
    setSaved(status === 'verified' ? 'Approved!' : status === 'unverified' ? 'Rejected' : 'Updated')
    setTimeout(() => setSaved(''), 2500)
  }

  const counts = {
    all:        sellers.length,
    pending:    sellers.filter(s => s.verification_status === 'pending').length,
    verified:   sellers.filter(s => s.verification_status === 'verified').length,
    unverified: sellers.filter(s => s.verification_status === 'unverified').length,
  }

  const filtered = sellers.filter(s => {
    const matchFilter = filter === 'all' || s.verification_status === filter
    const q = search.toLowerCase()
    const matchSearch = !search || s.shop_name?.toLowerCase().includes(q) || s.owner_name?.toLowerCase().includes(q) || s.email?.toLowerCase().includes(q)
    return matchFilter && matchSearch
  })

  const timeAgo = (d) => {
    const s = Math.floor((new Date() - new Date(d)) / 1000)
    if (s < 3600) return Math.floor(s / 60) + 'm ago'
    if (s < 86400) return Math.floor(s / 3600) + 'h ago'
    return Math.floor(s / 86400) + 'd ago'
  }

  const docsScore = (s) => {
    const fields = [s.owner_name, s.nid_number, s.trade_license, s.owner_address, s.owner_facebook_url]
    return fields.filter(Boolean).length + '/' + fields.length
  }

  return (
    <div style={{ display: 'flex', minHeight: '100vh', background: BG, fontFamily: 'Space Grotesk, system-ui, sans-serif' }}>
      <AdminNav />
      <div style={{ flex: 1, overflowY: 'auto', overflowX: 'hidden' }}>
        <div style={{ padding: '24px 28px' }}>
          <style>{`
            @media(max-width:900px){ .verify-layout{flex-direction:column!important} .verify-list{width:100%!important; max-height:400px} }
          `}</style>

          {/* Header */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px', flexWrap: 'wrap', gap: '10px' }}>
            <div>
              <div style={{ fontSize: '20px', fontWeight: 800, color: TEXT, letterSpacing: '-0.3px' }}>Verifications</div>
              <div style={{ fontSize: '12.5px', color: MUTED, marginTop: '2px' }}>
                {counts.pending > 0 && <span style={{ color: BLUE, fontWeight: 700 }}>{counts.pending} pending · </span>}
                {counts.verified} approved · {counts.unverified} rejected
              </div>
            </div>
            <button onClick={fetchAll} style={{ fontSize: '12px', fontWeight: 600, color: MUTED, background: WHITE, border: '1px solid ' + BORDER, padding: '7px 14px', borderRadius: '9px', cursor: 'pointer', fontFamily: 'Space Grotesk, sans-serif' }}>
              ↻ Refresh
            </button>
          </div>

          {loading && (
            <div style={{ textAlign: 'center', padding: '60px', color: MUTED, fontSize: '13px' }}>Loading verification requests...</div>
          )}

          {!loading && (
            <div style={{ display: 'flex', gap: '16px', alignItems: 'flex-start' }} className="verify-layout">

              {/* LEFT: List */}
              <div style={{ width: '340px', flexShrink: 0, display: 'flex', flexDirection: 'column', gap: '10px' }} className="verify-list">

                {/* Filters */}
                <div style={{ background: WHITE, borderRadius: '12px', border: '1px solid ' + BORDER, padding: '12px' }}>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: '4px', marginBottom: '10px' }}>
                    {[
                      { key: 'all',        label: 'All',      accent: MUTED },
                      { key: 'pending',    label: 'Pending',  accent: BLUE  },
                      { key: 'verified',   label: 'Approved', accent: GREEN },
                      { key: 'unverified', label: 'Rejected', accent: RED   },
                    ].map(f => (
                      <button key={f.key} onClick={() => setFilter(f.key)}
                        style={{ padding: '6px 4px', borderRadius: '8px', border: 'none', fontSize: '10px', fontWeight: 700, cursor: 'pointer', background: filter === f.key ? f.accent : BG, color: filter === f.key ? WHITE : MUTED, fontFamily: 'Space Grotesk, sans-serif', transition: 'all 0.12s', textAlign: 'center' }}>
                        {f.label}
                        <span style={{ display: 'block', fontSize: '14px', fontWeight: 800, lineHeight: 1.2 }}>{counts[f.key]}</span>
                      </button>
                    ))}
                  </div>
                  <div style={{ position: 'relative' }}>
                    <input type="text" placeholder="Search shop or owner..." value={search}
                      onChange={e => setSearch(e.target.value)}
                      style={{ width: '100%', border: '1px solid ' + BORDER, borderRadius: '9px', padding: '7px 12px 7px 30px', fontSize: '12px', outline: 'none', background: BG, color: TEXT, fontFamily: 'Space Grotesk, sans-serif', boxSizing: 'border-box' }}
                      onFocus={e => e.target.style.borderColor = BLUE}
                      onBlur={e => e.target.style.borderColor = BORDER} />
                    <div style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)' }}>
                      <Icon path={IC.search} color={MUTED} size={13} />
                    </div>
                  </div>
                </div>

                {/* Seller list */}
                <div style={{ background: WHITE, borderRadius: '12px', border: '1px solid ' + BORDER, overflow: 'hidden' }}>
                  {filtered.length === 0 ? (
                    <div style={{ padding: '40px', textAlign: 'center', color: MUTED, fontSize: '12px' }}>No requests found</div>
                  ) : filtered.map((s, i) => {
                    const vs = VERIFY_STYLE[s.verification_status] || VERIFY_STYLE.pending
                    const isSelected = selected?.id === s.id
                    return (
                      <div key={s.id}
                        onClick={() => setSelected(s)}
                        style={{ padding: '12px 14px', borderBottom: i < filtered.length - 1 ? '1px solid #F5F5F5' : 'none', cursor: 'pointer', background: isSelected ? '#EFF6FF' : WHITE, borderLeft: isSelected ? '3px solid ' + BLUE : '3px solid transparent', transition: 'all 0.1s' }}
                        onMouseEnter={e => { if (!isSelected) e.currentTarget.style.background = BG }}
                        onMouseLeave={e => { if (!isSelected) e.currentTarget.style.background = WHITE }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '3px' }}>
                          <span style={{ fontSize: '13px', fontWeight: 700, color: TEXT, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1, marginRight: '8px' }}>{s.shop_name}</span>
                          <span style={{ fontSize: '10px', fontWeight: 700, padding: '2px 7px', borderRadius: '20px', background: vs.bg, color: vs.color, flexShrink: 0 }}>{vs.label}</span>
                        </div>
                        <div style={{ fontSize: '11.5px', color: MUTED, marginBottom: '2px' }}>{s.owner_name || 'No owner name'}</div>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                          <span style={{ fontSize: '11px', color: MUTED }}>{s.verification_requested_at ? timeAgo(s.verification_requested_at) : '—'}</span>
                          <span style={{ fontSize: '10.5px', fontWeight: 600, color: MUTED }}>Docs: {docsScore(s)}</span>
                        </div>
                      </div>
                    )
                  })}
                </div>
              </div>

              {/* RIGHT: Detail */}
              {selected && (
                <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: '12px' }}>

                  {/* Header */}
                  <div style={{ background: WHITE, borderRadius: '12px', border: '1px solid ' + BORDER, padding: '18px 20px' }}>
                    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '12px', flexWrap: 'wrap' }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: '18px', fontWeight: 800, color: TEXT, marginBottom: '2px' }}>{selected.shop_name}</div>
                        <div style={{ fontSize: '12px', color: MUTED, marginBottom: '8px' }}>{selected.email}</div>
                        <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                          <span style={{ fontSize: '11px', fontWeight: 700, padding: '3px 10px', borderRadius: '20px', background: VERIFY_STYLE[selected.verification_status]?.bg, color: VERIFY_STYLE[selected.verification_status]?.color }}>
                            {VERIFY_STYLE[selected.verification_status]?.label}
                          </span>
                          {selected.facebook_url && (
                            <a href={selected.facebook_url} target="_blank" rel="noreferrer"
                              style={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: '12px', fontWeight: 600, color: BLUE, background: '#EFF6FF', border: 'none', padding: '4px 10px', borderRadius: '8px', textDecoration: 'none' }}>
                              <Icon path={IC.fb} color={BLUE} size={12} sw={2} />
                              FB Page
                            </a>
                          )}
                          {saved && <span style={{ fontSize: '12px', fontWeight: 700, color: saved === 'Approved!' ? GREEN : RED }}>{saved}</span>}
                        </div>
                      </div>
                      <button onClick={() => setSelected(null)} style={{ fontSize: '12px', fontWeight: 600, color: MUTED, background: BG, border: '1px solid ' + BORDER, padding: '7px 12px', borderRadius: '8px', cursor: 'pointer', flexShrink: 0, fontFamily: 'Space Grotesk, sans-serif' }}>
                        ✕ Close
                      </button>
                    </div>
                  </div>

                  {/* Shop info + Verification docs */}
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                    <div style={{ background: WHITE, borderRadius: '12px', border: '1px solid ' + BORDER, padding: '16px 18px' }}>
                      <div style={{ fontSize: '11px', fontWeight: 700, color: MUTED, textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: '12px' }}>Shop info</div>
                      {[
                        { label: 'Phone',    value: selected.phone },
                        { label: 'District', value: selected.district },
                        { label: 'Category', value: selected.category },
                        { label: 'Region',   value: selected.seller_mode === 'international' ? 'International' : 'Bangladesh' },
                        { label: 'Plan',     value: selected.subscription_plan || selected.plan || 'trial' },
                      ].map(({ label, value }) => value && (
                        <div key={label} style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                          <span style={{ fontSize: '12px', color: MUTED }}>{label}</span>
                          <span style={{ fontSize: '12px', fontWeight: 600, color: TEXT, textTransform: 'capitalize' }}>{value}</span>
                        </div>
                      ))}
                      {selected.verification_requested_at && (
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                          <span style={{ fontSize: '12px', color: MUTED }}>Requested</span>
                          <span style={{ fontSize: '12px', color: MUTED }}>{new Date(selected.verification_requested_at).toLocaleDateString('en-BD', { day: 'numeric', month: 'short', year: 'numeric' })}</span>
                        </div>
                      )}
                    </div>

                    <div style={{ background: WHITE, borderRadius: '12px', border: '1px solid ' + BORDER, padding: '16px 18px' }}>
                      <div style={{ fontSize: '11px', fontWeight: 700, color: MUTED, textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: '12px' }}>
                        Submitted documents — {docsScore(selected)}
                      </div>
                      <DocRow label="Owner name"    value={selected.owner_name} />
                      <DocRow label="NID / Passport" value={selected.nid_number || selected.owner_nid} mono />
                      <DocRow label="Trade license"  value={selected.trade_license} mono />
                      <DocRow label="Owner address"  value={selected.owner_address} />
                      {selected.owner_facebook_url && (
                        <div style={{ padding: '9px 12px', background: '#EFF6FF', borderRadius: '8px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <span style={{ fontSize: '12px', color: MUTED }}>Owner Facebook</span>
                          <a href={selected.owner_facebook_url} target="_blank" rel="noreferrer"
                            style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '12px', fontWeight: 600, color: BLUE, textDecoration: 'none' }}>
                            View <Icon path={IC.ext} color={BLUE} size={11} sw={2} />
                          </a>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Decision buttons */}
                  <div style={{ background: WHITE, borderRadius: '12px', border: '1px solid ' + BORDER, padding: '18px 20px' }}>
                    <div style={{ fontSize: '11px', fontWeight: 700, color: MUTED, textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: '14px' }}>Verification decision</div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '10px' }}>
                      <button
                        onClick={() => updateVerification(selected.id, 'unverified')}
                        disabled={updating || selected.verification_status === 'unverified'}
                        style={{ padding: '14px', borderRadius: '10px', border: '2px solid ' + (selected.verification_status === 'unverified' ? RED : BORDER), background: selected.verification_status === 'unverified' ? '#FEF2F2' : WHITE, fontSize: '13px', fontWeight: 700, color: RED, cursor: selected.verification_status === 'unverified' ? 'default' : 'pointer', opacity: selected.verification_status === 'unverified' ? 0.6 : 1, fontFamily: 'Space Grotesk, sans-serif' }}>
                        <div style={{ fontSize: '20px', marginBottom: '4px' }}>✗</div>
                        Reject
                      </button>
                      <button
                        onClick={() => updateVerification(selected.id, 'pending')}
                        disabled={updating || selected.verification_status === 'pending'}
                        style={{ padding: '14px', borderRadius: '10px', border: '2px solid ' + (selected.verification_status === 'pending' ? BLUE : BORDER), background: selected.verification_status === 'pending' ? '#EFF6FF' : WHITE, fontSize: '13px', fontWeight: 700, color: BLUE, cursor: selected.verification_status === 'pending' ? 'default' : 'pointer', opacity: selected.verification_status === 'pending' ? 0.6 : 1, fontFamily: 'Space Grotesk, sans-serif' }}>
                        <div style={{ fontSize: '20px', marginBottom: '4px' }}>⏳</div>
                        Pending
                      </button>
                      <button
                        onClick={() => updateVerification(selected.id, 'verified')}
                        disabled={updating || selected.verification_status === 'verified'}
                        style={{ padding: '14px', borderRadius: '10px', border: '2px solid ' + (selected.verification_status === 'verified' ? GREEN : BORDER), background: selected.verification_status === 'verified' ? '#F0FDF4' : WHITE, fontSize: '13px', fontWeight: 700, color: GREEN, cursor: selected.verification_status === 'verified' ? 'default' : 'pointer', opacity: selected.verification_status === 'verified' ? 0.6 : 1, fontFamily: 'Space Grotesk, sans-serif' }}>
                        <div style={{ fontSize: '20px', marginBottom: '4px' }}>✅</div>
                        Approve
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {!selected && (
                <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', background: WHITE, borderRadius: '12px', border: '1px solid ' + BORDER, minHeight: '300px' }}>
                  <div style={{ textAlign: 'center', color: MUTED }}>
                    <div style={{ fontSize: '32px', marginBottom: '10px' }}>🔍</div>
                    <div style={{ fontSize: '13px', fontWeight: 600, color: TEXT, marginBottom: '4px' }}>Select a request</div>
                    <div style={{ fontSize: '12px' }}>Click any seller to review their verification</div>
                  </div>
                </div>
              )}

            </div>
          )}

        </div>
      </div>
    </div>
  )
}

export default AdminVerifications